import { z } from "zod";
import { isoDateTimeSchema, uuidSchema } from "./common.js";

export const connectionGroupSchema = z.enum(["utility", "market", "reference", "platform"]);
export const directionSchema = z.enum(["in", "out", "both"]);
export const connectorKindSchema = z.enum([
  "file",
  "api",
  "oidc",
  "object_store",
  "job_object",
  "tiles",
  "market_port",
]);
export const buildStatusSchema = z.enum(["live", "simulated", "planned"]);
export const connectionHealthSchema = z.enum([
  "connected",
  "connected_simulated",
  "attention",
  "planned",
]);
export type ConnectionHealth = z.infer<typeof connectionHealthSchema>;

/** One entry of config/connections.yaml. */
export const connectionEntrySchema = z
  .object({
    id: z.string().regex(/^[a-z]+$/),
    name: z.string().min(1),
    group: connectionGroupSchema,
    direction: directionSchema,
    data: z.string(),
    channel: z.string(),
    frequency: z.string(),
    connector: connectorKindSchema,
    read_only: z.boolean(),
    build_status: buildStatusSchema,
    used_by: z.array(z.string()),
    templates: z.array(z.string()).optional(),
    why: z.string(),
  })
  .refine((c) => !(c.read_only && c.direction === "out"), {
    message: "a read-only connection cannot be outbound",
  })
  .refine((c) => !((c.id === "hes" || c.id === "mdm") && !c.read_only), {
    message: "head-end and MDM connections are read only (D-21)",
  });
export type ConnectionEntry = z.infer<typeof connectionEntrySchema>;

export const connectionsFileSchema = z.object({
  version: z.literal(1),
  connections: z.array(connectionEntrySchema).min(1),
  not_connected: z.array(z.object({ name: z.string(), reason: z.string() })),
});
export type ConnectionsFile = z.infer<typeof connectionsFileSchema>;

/** GET /connections item. */
export const connectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  group: connectionGroupSchema,
  direction: directionSchema,
  data: z.string(),
  channel: z.string(),
  frequency: z.string(),
  connector: connectorKindSchema,
  readOnly: z.boolean(),
  buildStatus: buildStatusSchema,
  health: connectionHealthSchema,
  lastActivityAt: isoDateTimeSchema.nullable(),
  lastError: z.string().nullable(),
  usedBy: z.array(z.string()),
  why: z.string(),
  templates: z.array(z.string()),
});
export type Connection = z.infer<typeof connectionSchema>;

export const connectionsResponseSchema = z.object({
  items: z.array(connectionSchema),
  notConnected: z.array(z.object({ name: z.string(), reason: z.string() })),
});

export const connectionSyncSchema = z.object({
  id: uuidSchema,
  connectionId: z.string(),
  trigger: z.enum(["schedule", "manual"]),
  status: z.enum(["running", "completed", "failed"]),
  startedAt: isoDateTimeSchema,
  finishedAt: isoDateTimeSchema.nullable(),
  counts: z.record(z.number().int()),
  error: z.string().nullable(),
});
export type ConnectionSync = z.infer<typeof connectionSyncSchema>;
